import { useEffect, useState } from 'react'
import {
  deliveryAreasApi, occasionsApi, deliveryListApi,
  type DeliveryArea, type DeliveryOccasion, type DeliveryList as DeliveryListData,
} from '../crmApi'
import { ApiError, downloadFile } from '../api'

export function DeliveryList() {
  const [areas, setAreas] = useState<DeliveryArea[]>([])
  const [areaId, setAreaId] = useState<number | null>(null)
  const [occasions, setOccasions] = useState<DeliveryOccasion[]>([])
  const [occasionId, setOccasionId] = useState<number | null>(null)
  const [data, setData] = useState<DeliveryListData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fail = (e: unknown) => setError(e instanceof ApiError ? `Fel ${e.status}: ${e.body}` : String(e))

  useEffect(() => {
    deliveryAreasApi.list()
      .then((a) => { setAreas(a); setError(null); if (a.length) setAreaId((id) => id ?? a[0].id) })
      .catch(fail)
  }, [])

  // Occasions (most recent first — deliveries are for current/recent dates).
  useEffect(() => {
    if (areaId == null) return
    setOccasions([]); setOccasionId(null); setData(null)
    occasionsApi.list(areaId)
      .then((o) => {
        const sorted = [...o].sort((a, b) => b.date.localeCompare(a.date))
        setOccasions(sorted); setError(null)
        if (sorted.length) setOccasionId(sorted[0].id)
      })
      .catch(fail)
  }, [areaId])

  useEffect(() => {
    if (occasionId == null) { setData(null); return }
    setLoading(true)
    deliveryListApi.get(occasionId)
      .then((d) => { setData(d); setError(null) })
      .catch(fail)
      .finally(() => setLoading(false))
  }, [occasionId])

  async function printPdf() {
    if (occasionId == null || data == null) return
    try { await downloadFile(deliveryListApi.pdfPath(occasionId), `leveranslista-${data.date}.pdf`) }
    catch (e) { fail(e) }
  }

  return (
    <div>
      <h1>Leveranslista</h1>
      <p className="muted">Packlista och leveranslista i leveransordning för ett leveranstillfälle (§6.19).</p>
      {error && <p className="error">{error}</p>}

      <div className="card filters">
        <label>
          Leveransområde{' '}
          <select value={areaId ?? ''} onChange={(e) => setAreaId(Number(e.target.value))}>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        <label>
          Leveranstillfälle{' '}
          <select value={occasionId ?? ''} disabled={occasions.length === 0} onChange={(e) => setOccasionId(Number(e.target.value))}>
            {occasions.length === 0 && <option value="">Inga leveranstillfällen</option>}
            {occasions.map((o) => <option key={o.id} value={o.id}>{o.date}{o.isClosed ? ' (stängt)' : ''}</option>)}
          </select>
        </label>
        <button className="primary" disabled={data == null} onClick={() => void printPdf()}>Skriv ut (PDF)</button>
      </div>

      {loading ? <p className="muted">Laddar…</p> : data == null ? null : (
        <>
          <div className="card">
            <h2>Packlista</h2>
            {data.pickingList.length === 0 ? <p className="muted">Inga varor att lasta.</p> : (
              <table className="grid">
                <thead><tr><th>Produkt</th><th>Antal</th></tr></thead>
                <tbody>
                  {data.pickingList.map((p) => (
                    <tr key={p.productId}><td>{p.productName}</td><td>{p.totalUnits}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <h2>Leveranser ({data.stops.length})</h2>
          {data.stops.length === 0 && <p className="muted">Inga leveranser för det här tillfället.</p>}
          {data.stops.map((s, i) => (
            <div key={s.customerId} className="card">
              <div className="row">
                <strong>{i + 1}. {s.customerName}</strong>
                {s.hasPaperInvoice && <span className="badge">Pappersfaktura</span>}
              </div>
              <div className="muted">{[s.deliveryStreet, [s.deliveryPostalCode, s.deliveryCity].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</div>
              <div className="muted">Tel: {[s.homePhone, s.mobilePhone, s.workPhone].filter(Boolean).join(', ')}</div>
              <ul>
                {s.rows.map((r) => <li key={r.productId}>{r.numberOfUnits} × {r.productName}</li>)}
              </ul>
              {s.deliveryNote && <div className="muted"><em>Not: {s.deliveryNote}</em></div>}
            </div>
          ))}
        </>
      )}
    </div>
  )
}
